import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
	Box,
	Typography,
	Container,
	CircularProgress,
	Button
} from '@mui/material';
import QuestionnaireComponent from './QuestionnaireComponent';
import ConversationBlock from './ConversationBlock';
import StimuliComponent from './StimuliComponent';
import PrimedConversationBlock from './PrimedConversationBlock';
import Instruction from './Instruction';
import axios from 'axios';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
	initEventTracking,
	getMetadata,
	clearMetadata,
	registerClientEventEmitter,
	updateClientEventContext,
	unregisterClientEventEmitter,
} from './metadataTracker';
import { resolveMediaUrl } from './stimulusOrder';

// Simple environment guard & CSS variable injector
function detectDeviceCategory() {
	if (typeof window === 'undefined') return 'unknown';
	const w = window.innerWidth;
	const h = window.innerHeight;
	const ua = (navigator.userAgent || '').toLowerCase();
	const qp = new URLSearchParams(window.location.search);
	if (qp.get('forceDevice') === 'desktop') return 'desktop';
	const pointerCoarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
	const mobileUA = /(iphone|android|ipod|blackberry|windows phone)/i.test(ua);
	const tabletUA = /(ipad|tablet|sm-t\d|sm-p\d|kindle|silk)/i.test(ua);
	const belowThreshold = w < 1000 || h < 600;
	if (mobileUA || tabletUA) return 'mobile-like';
	if (belowThreshold && pointerCoarse) return 'mobile-like';
	if (w < 820 || h < 550) return 'mobile-like';
	return 'desktop';
}

const EnvironmentGuard = ({ onEnvironmentInfo, children }) => {
	const [env, setEnv] = useState(null);
	useEffect(() => {
		const info = {
			userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
			width: typeof window !== 'undefined' ? window.innerWidth : null,
			height: typeof window !== 'undefined' ? window.innerHeight : null,
			dpr: typeof window !== 'undefined' ? window.devicePixelRatio : null,
			deviceCategory: detectDeviceCategory(),
			timestamp: new Date().toISOString()
		};
		setEnv(info);
		onEnvironmentInfo && onEnvironmentInfo(info);
	}, [onEnvironmentInfo]);

	if (!env) return null; // brief flash acceptable
		if (env.deviceCategory === 'mobile-like') {
		return (
			<Box sx={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', p:3 }}>
				<Container maxWidth="sm" sx={{ bgcolor:'#fff', p:3, borderRadius:1, boxShadow:2 }}>
					<Typography variant="h6" gutterBottom>Unsupported Device</Typography>
					<Typography variant="body2" gutterBottom>
							This experiment must be completed on a desktop or laptop (minimum recommended viewport 1024×700). It looks like you may be on a phone or tablet. If this is incorrect, enlarge your window or add ?forceDevice=desktop to the URL.
					</Typography>
				</Container>
			</Box>
		);
	}
	return children;
};

function applyCSSVariables(info) {
	if (typeof document === 'undefined') return;
	const root = document.documentElement;
	root.style.setProperty('--stimulus-max-height', '60vh');
	root.style.setProperty('--content-max-width', '1200px');
	root.style.setProperty('--questions-max-width', '900px');
	root.style.setProperty('--base-font-size', '16px');
	root.style.setProperty('--exp-gap-md', '16px');
	const calibrationScale = info?.calibration?.scaleFactor;
	root.style.setProperty(
		'--stimulus-scale-factor',
		calibrationScale && calibrationScale > 0 ? String(1 / calibrationScale) : '1'
	);
	if (info?.deviceCategory === 'desktop') {
		root.style.setProperty('--exp-density-scale', '1');
	}
}

const theme = createTheme({
	palette: { background: { default: '#f0f0f0' } },
	typography: { fontFamily: 'Georgia, serif' },
});

// Use environment variable for API URL
const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

function Experiment() {
	const { exptID } = useParams();
	const [searchParams] = useSearchParams();
	const uid = searchParams.get('uid');
	const [sessionData, setSessionData] = useState({
		userID: null,
		roomID: null,
		exptConfig: null,
		messages: {},
		currentBlock: null,
		currentTrial: null
	}); // Holds all relevant session data
	const [loading, setLoading] = useState(true); // For GET request
	const [startLoading, setStartLoading] = useState(false); // For POST request
	const [error, setError] = useState(null);
	const [started, setStarted] = useState(false); // Experiment started?
	const [ended, setEnded] = useState(false);     // Experiment ended?
	const [currentBlockIndex, setCurrentBlockIndex] = useState(1);
	const [conversationEnded, setConversationEnded] = useState(false);
	const [blockStage, setBlockStage] = useState('intro');
	const [pendingResponses, setPendingResponses] = useState(null);
	// removed unused variantInfo state
	const [variantBlockCount, setVariantBlockCount] = useState(null);
	const [environmentInfo, setEnvironmentInfo] = useState(null);
	const [preflightStage, setPreflightStage] = useState(null); // 'fullscreen' | 'calibration' | null
	const [preflightCompleted, setPreflightCompleted] = useState(false);
	// removed unused calibration state
	// Stimuli preload tracking
	const [preloadProgress, setPreloadProgress] = useState(0);
	const [preloading, setPreloading] = useState(false);
	const [preloadDone, setPreloadDone] = useState(false);
	const [advancingBlock, setAdvancingBlock] = useState(false);
	const stimulusEventDedupRef = useRef(new Map());

	// A7 — stimulus-event poster. Fire-and-forget; never blocks the
	// participant. Mirrors postPreflight (below) but with stimulus
	// scope: `stimIndex` and `stimulusId` ride alongside the canonical
	// {kind, payload, blockNo} fields so the data team can join events
	// against the corresponding `blockResponses.stimuli_responses` rows.
	const postStimulusEvent = React.useCallback((kind, payload = null, scope = {}) => {
		if (!sessionData.userID || !sessionData.roomID) return;
		try {
			// Defensive backstop: if a UI effect accidentally re-fires during
			// state updates, do not spam identical stimulus telemetry requests.
			// This protects the browser from ERR_INSUFFICIENT_RESOURCES and keeps
			// media downloads from being starved by repeated fire-and-forget POSTs.
			const eventKey = JSON.stringify({
				kind,
				blockNo: currentBlockIndex,
				stimIndex: typeof scope.stimIndex === 'number' ? scope.stimIndex : null,
				stimulusId: typeof scope.stimulusId === 'string' ? scope.stimulusId : null,
				payload,
			});
			const now = Date.now();
			const lastSentAt = stimulusEventDedupRef.current.get(eventKey) || 0;
			if (now - lastSentAt < 1000) return;
			stimulusEventDedupRef.current.set(eventKey, now);
			if (stimulusEventDedupRef.current.size > 200) {
				for (const [key, ts] of stimulusEventDedupRef.current.entries()) {
					if (now - ts > 60_000) stimulusEventDedupRef.current.delete(key);
				}
			}
			axios.post(
				`${API_URL}/api/experiment/${exptID}/stimulus-event`,
				{
					userID: sessionData.userID,
					roomID: sessionData.roomID,
					kind,
					payload,
					blockNo: currentBlockIndex,
					stimIndex: typeof scope.stimIndex === 'number' ? scope.stimIndex : null,
					stimulusId: typeof scope.stimulusId === 'string' ? scope.stimulusId : null,
				},
				{ withCredentials: true },
			).catch(err => console.warn('[stimulus-event] post failed', kind, err?.message));
		} catch (e) { /* never let telemetry throw */ }
	}, [exptID, sessionData.userID, sessionData.roomID, currentBlockIndex]);

	// A10 — GSA-event poster. Mirrors postStimulusEvent. The server
	// persists `update` and `error` kinds from socket/index.js when
	// EG-GSA scoring resolves; this UI helper handles the `shown` kind
	// (fired by ChatComponent.js when an Explanation bubble actually
	// renders) and any future UI-driven GSA telemetry.
	const postGsaEvent = React.useCallback((kind, payload = null) => {
		if (!sessionData.userID || !sessionData.roomID) return;
		try {
			axios.post(
				`${API_URL}/api/experiment/${exptID}/gsa-event`,
				{
					userID: sessionData.userID,
					roomID: sessionData.roomID,
					kind,
					payload,
					blockNo: currentBlockIndex,
					occurred_at: new Date().toISOString(),
				},
				{ withCredentials: true },
			).catch(err => console.warn('[gsa-event] post failed', kind, err?.message));
		} catch (e) { /* never let telemetry throw */ }
	}, [exptID, sessionData.userID, sessionData.roomID, currentBlockIndex]);

	const preloadStimuliForBlock = async (block) => {
		if (!block || block.type !== 'stimuli') return true;
		const items = block.stimuli || [];
		if (!items.length) return true;
		console.debug('[stimuli-preload] starting', items.length, 'items');
		setPreloading(true); setPreloadProgress(0); setPreloadDone(false);
		let completed = 0;
		const update = () => { completed += 1; setPreloadProgress(completed / items.length); };
		// A7 — wrap each preload task so we can record duration + success/error
		// on the session document. Posts are fire-and-forget so they never
		// block the preload pipeline.
		const wrap = (idx, item, url, cat, p) => {
			const startedAt = performance.now();
			return p.then((r) => {
				// `null` here means a fetch caught; the original code swallows it.
				// We surface that as a preload_error so the data team can see it.
				const ok = r !== null && r !== false;
				const durationMs = Math.round(performance.now() - startedAt);
				const stimulusId = item?.id || item?._id || item?.mediaMeta?.id || `stim-${idx}`;
				if (ok) {
					postStimulusEvent('preload_complete', {
						category: cat || null,
						url: typeof url === 'string' ? url : null,
						durationMs,
					}, { stimIndex: idx, stimulusId });
				} else {
					postStimulusEvent('preload_error', {
						category: cat || null,
						url: typeof url === 'string' ? url : null,
						durationMs,
						errorMsg: 'fetch_failed',
					}, { stimIndex: idx, stimulusId });
				}
				return r;
			});
		};
		const createTask = (item, idx) => async () => {
			const url = resolveMediaUrl(item.media || item.url, API_URL);
			const cat = item.mediaMeta?.category || item.category;
			const stimulusId = item?.id || item?._id || item?.mediaMeta?.id || `stim-${idx}`;
			if (!url) {
				console.warn('[stimuli-preload] item missing media/url', item);
				postStimulusEvent('preload_error', { category: cat || null, url: null, errorMsg: 'missing_url' }, { stimIndex: idx, stimulusId });
				update();
				return Promise.resolve();
			}
			// Choose strategy by mimetype/category
			if (cat === 'image' || /image\//.test(item.mediaMeta?.mimetype||'')) {
				return wrap(idx, item, url, cat, new Promise(res => { const img = new Image(); img.decoding = 'async'; img.onload = () => { update(); res(true); }; img.onerror = () => { console.warn('[stimuli-preload] image failed', url); update(); res(null); }; img.src = url; }));
			}
			if (cat === 'pdf' || /pdf$/.test(url) || item.mediaMeta?.mimetype === 'application/pdf') {
				return wrap(idx, item, url, cat, fetch(url, { method:'HEAD' }).then(r => r?.ok ? true : null).catch(()=>null).finally(()=> update()));
			}
			if (cat === 'audio' || /audio\//.test(item.mediaMeta?.mimetype||'')) {
				return wrap(idx, item, url, cat, fetch(url, { headers:{ Range: 'bytes=0-1024' }}).then(r => r?.ok ? true : null).catch(()=>null).finally(()=> update()));
			}
			if (cat === 'video' || /video\//.test(item.mediaMeta?.mimetype||'')) {
				return wrap(idx, item, url, cat, fetch(url, { headers:{ Range: 'bytes=0-2048' }}).then(r => r?.ok ? true : null).catch(()=>null).finally(()=> update()));
			}
			// Text or generic
			return wrap(idx, item, url, cat, fetch(url, { method:'GET' }).then(r => r?.ok ? true : null).catch(()=>null).finally(()=> update()));
		};
		const tasks = items.map(createTask);
		const concurrency = Math.min(2, tasks.length);
		let nextTask = 0;
		const workers = Array.from({ length: concurrency }, async () => {
			while (nextTask < tasks.length) {
				const task = tasks[nextTask];
				nextTask += 1;
				await task();
			}
		});
		await Promise.all(workers);
		console.debug('[stimuli-preload] complete');
		setPreloading(false); setPreloadDone(true); setPreloadProgress(1);
		return true;
	};

	// Fetch session data when the component mounts.
	useEffect(() => {
		const fetchSession = async () => {
			try {
				if (sessionData.roomID !== null) {
					console.log("Session data already exists.");
					return;
				}
				console.log("Sending GET request to fetch session data.");
				// Include uid query parameter if provided
				const url = uid
					? `${API_URL}/api/experiment/${exptID}?uid=${encodeURIComponent(uid)}`
					: `${API_URL}/api/experiment/${exptID}`;
				axios.get(url, { withCredentials: true })
					.then(response => {
						console.log("API response:", response.data);
						const { userID, roomID, exptConfig, exptData: rawData, status, lastBlockNo, materialized, variant, variantBlockCount } = response.data;
						if (!userID || !roomID) {
							throw new Error('Missing userID or roomID');
						}
						// prefer materialized blocks for runtime operations
						const runtimeBlocks = materialized?.task?.blocks || exptConfig?.task?.blocks || [];
						// Build normalized messages map
						const msgsMap = {};
						if (rawData && typeof rawData === 'object') {
							Object.entries(rawData).forEach(([block, data]) => {
								if (Array.isArray(data)) {
									msgsMap[block] = convertMessages(data.filter(m => m.sender !== 'SYS'));
								}
							});
						}
						const startIndex = status === 'session_exists' ? lastBlockNo : 0;
						setSessionData({ userID, roomID, exptConfig: { ...exptConfig, task: { ...exptConfig.task, blocks: runtimeBlocks } }, messages: msgsMap });
						// variant info is logged but not stored in state
						setVariantBlockCount(variantBlockCount || runtimeBlocks.length);
						console.debug('[experiment] assigned variant', variant?.id, 'hash', variant?.variant_hash);
						console.debug('[experiment] materialized block order:', runtimeBlocks.map(b => ({ id: b.id, type: b.type, title: b.title })));

						setCurrentBlockIndex(startIndex);
						if (status === 'session_exists') setStarted(true);
					})
					.finally(() => setLoading(false));
			} catch (err) {
				console.error('Error fetching session data:', err);
				setError('Failed to create session. Please try again.');
				setLoading(false); // Ensure loading is stopped on error
			}
		};
		fetchSession();
	}, [exptID, sessionData.roomID, uid]);

	useEffect(() => {
		// Start capturing metadata as soon as the component renders.
		initEventTracking();
	}, []);

	// A8 — wire metadataTracker to POST typed client events to the server
	// once we know the session identity. The hooks were registered earlier
	// by initEventTracking; this just gives them somewhere to send to.
	useEffect(() => {
		if (!exptID || !sessionData.userID || !sessionData.roomID) return undefined;
		registerClientEventEmitter({
			exptID,
			userID: sessionData.userID,
			roomID: sessionData.roomID,
			blockNo: currentBlockIndex,
		});
		return () => {
			unregisterClientEventEmitter();
		};
	}, [exptID, sessionData.userID, sessionData.roomID]); // eslint-disable-line react-hooks/exhaustive-deps

	// A8 — keep the live emitter's blockNo in sync as the participant
	// advances. Cheap shallow-merge; no re-registration overhead.
	useEffect(() => {
		updateClientEventContext({ blockNo: currentBlockIndex });
	}, [currentBlockIndex]);

	useEffect(() => {
		if (environmentInfo) {
			applyCSSVariables(environmentInfo);
		}
	}, [environmentInfo]);

	// A6 — preflight event persistence. Fire-and-forget POST to the server
	// so a participant who closes their tab mid-preflight still leaves an
	// audit trail. `currentBlockIndex` is captured at call time so the
	// reader can slice events by block.
	const postPreflight = React.useCallback((kind, payload = null) => {
		if (!sessionData.userID || !sessionData.roomID) return;
		try {
			axios.post(
				`${API_URL}/api/experiment/${exptID}/preflight`,
				{
					userID: sessionData.userID,
					roomID: sessionData.roomID,
					kind,
					payload,
					blockNo: currentBlockIndex,
				},
				{ withCredentials: true },
			).catch(err => console.warn('[preflight] post failed', kind, err?.message));
		} catch (e) { /* never let preflight throw */ }
	}, [exptID, sessionData.userID, sessionData.roomID, currentBlockIndex]);

	// Initialize preflight sequence once started
	useEffect(() => {
		if (started && !ended && !preflightCompleted && preflightStage === null) {
			setPreflightStage('fullscreen');
			postPreflight('preflight_started');
		}
	}, [started, ended, preflightStage, preflightCompleted, postPreflight]);

	// A6 — fullscreen change listener. Captures `fullscreen_exit` when the
	// participant leaves fullscreen mid-experiment (deliberately or via the
	// browser closing it). The enter event is recorded by `requestFullscreen`
	// below.
	useEffect(() => {
		if (!started || ended) return;
		const onChange = () => {
			if (document.fullscreenElement) return; // entered — handled by requestFullscreen()
			// We only fire fullscreen_exit *after* preflight completed, so the
			// initial "user hasn't entered fullscreen yet" state doesn't count.
			if (preflightCompleted) {
				postPreflight('fullscreen_exit', { ts: new Date().toISOString() });
			}
		};
		document.addEventListener('fullscreenchange', onChange);
		document.addEventListener('webkitfullscreenchange', onChange);
		document.addEventListener('mozfullscreenchange', onChange);
		document.addEventListener('MSFullscreenChange', onChange);
		return () => {
			document.removeEventListener('fullscreenchange', onChange);
			document.removeEventListener('webkitfullscreenchange', onChange);
			document.removeEventListener('mozfullscreenchange', onChange);
			document.removeEventListener('MSFullscreenChange', onChange);
		};
	}, [started, ended, preflightCompleted, postPreflight]);

	const requestFullscreen = async () => {
		try {
			if (document.fullscreenElement) {
				setPreflightStage('calibration');
				return;
			}
			const el = document.documentElement;
			if (el.requestFullscreen) await el.requestFullscreen();
			else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
			else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
			else if (el.msRequestFullscreen) el.msRequestFullscreen();
			// A6: record successful entry (best-effort — we don't await the
			// server response because the participant should not be blocked).
			postPreflight('fullscreen_enter', { ts: new Date().toISOString() });
			setTimeout(()=> setPreflightStage('calibration'), 120);
		} catch (e) {
			console.warn('Fullscreen request failed', e);
			// A6: capture the failure path. We still advance to calibration so
			// the experiment can continue (some browsers refuse fullscreen
			// without a direct user click — we still want their calibration
			// data).
			postPreflight('fullscreen_failed', { error: e?.message || String(e) });
			setPreflightStage('calibration');
		}
	};

	const handleCalibrationConfirm = (cardPxWidth) => {
		const STANDARD_CARD_MM = 85.6;
		const mmPerPx = STANDARD_CARD_MM / cardPxWidth;
		const scaleFactor = mmPerPx / 0.26458;
		const data = { cardPxWidth, mmPerPx, scaleFactor, confirmedAt: new Date().toISOString() };
		setEnvironmentInfo(prev => ({ ...(prev||{}), calibration: data }));
		// A6: persist calibration immediately so a tab-close mid-experiment
		// still leaves a calibration record. The server also $sets
		// environmentInfo.calibration on the session doc for downstream
		// consumers (ExperimentDetails.js, export endpoint).
		postPreflight('calibration', data);
		postPreflight('preflight_completed');
		setPreflightStage(null);
		setPreflightCompleted(true);
	};

	const CalibrationScreen = ({ onConfirm }) => {
		const [width, setWidth] = useState(300);
		const STANDARD_CARD_MM = 85.6;
		const mmPerPx = STANDARD_CARD_MM / width;
		return (
			<Box sx={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', p:3 }}>
				<Container maxWidth="sm" sx={{ bgcolor:'#fff', p:3, borderRadius:1, boxShadow:2 }}>
					<Typography variant="h6" gutterBottom>Screen Calibration</Typography>
					<Typography variant="body2" gutterBottom>Hold a standard credit card against the rectangle. Adjust until sizes match exactly.</Typography>
					<Box sx={{ my:3, display:'flex', justifyContent:'center' }}>
						<Box sx={{ border:'2px solid #1976d2', height: Math.round(width*(53.98/85.6)), width, position:'relative', bgcolor:'#fafafa' }}>
							<Typography variant="caption" sx={{ position:'absolute', top:4, left:4, background:'#fff', px:0.5 }}>Card</Typography>
						</Box>
					</Box>
					<input type="range" min={150} max={800} value={width} onChange={e=> setWidth(parseInt(e.target.value,10))} style={{ width:'100%' }} />
					<Typography variant="caption" display="block" sx={{ mt:1 }}>Width: {width}px • mm/px: {mmPerPx.toFixed(3)} • Scale: {(mmPerPx/0.26458).toFixed(3)}</Typography>
					<Box sx={{ mt:3, textAlign:'right' }}>
						<Button variant="contained" onClick={()=> onConfirm(width)}>Continue</Button>
					</Box>
				</Container>
			</Box>
		);
	};

	useEffect(() => {
		console.log("Current block index changed:", currentBlockIndex);
		setConversationEnded(false);
	}, [currentBlockIndex])

	const convertMessages = (messages) => {
		if (!Array.isArray(messages)) {
			console.error('Invalid messages format:', messages);
			return [];
		}

		return messages.map((message) => ({
			sender: message.sender,
			text: message.msg
		}));
	}

	// post request to submit responses to blocks.
	const submitResponses = async (responses) => {
		try {
			await axios.post(
				`${API_URL}/api/experiment/${exptID}/block`,
				{
					userID: sessionData.userID,
					roomID: sessionData.roomID,
					responses,
					blockNo: currentBlockIndex,
				},
				{ withCredentials: true }
			);
			console.log('Responses submitted successfully:', responses);

		} catch (err) {
			console.error('Error submitting responses:', err);
			setError('Failed to submit responses. Please try again.');
		}
	};

	// post request to start a crew chat session.
	const startCrew = async (opts = {}) => {
		try {

			console.log("Starting crew chat with body:", sessionData);
			const isPrimed = sessionData.exptConfig?.task?.blocks?.[currentBlockIndex]?.type === 'primed_conversation';
			const mode = sessionData.exptConfig?.task?.blocks?.[currentBlockIndex]?.conversationMode;
			const stimQuery = (isPrimed && mode === 'per_stimulus' && typeof opts.stimIndex === 'number') ? `&s=${opts.stimIndex}` : '';
			const response = await axios.post(
				`${API_URL}/api/experiment/${sessionData.exptConfig.expt_id}/crew?b=${currentBlockIndex}${stimQuery}`,
				{
					userID: sessionData.userID,
					roomID: sessionData.roomID,
					exptConfig: sessionData.exptConfig,
					// Debug/audit only: lets the server compare the stimulus displayed by
					// the browser with block.stimuli[s] selected for the subprocess.
					displayStimIndex: typeof opts.displayStimIndex === 'number' ? opts.displayStimIndex : null,
					originalStimulusIndex: typeof opts.originalStimulusIndex === 'number' ? opts.originalStimulusIndex : null,
					stimulusId: opts.stimulusId || null,
					stimulusMedia: opts.stimulusMedia || null,
					stimulusLabel: opts.stimulusLabel || null,
				},
				{ withCredentials: true }
			);
			if (response.data.status === 'subprocess_started' || response.data.status === 'subprocess_running') {
				console.log('Crew chat started:', response.data);
				// conversationStarted state is not used elsewhere
			} else {
				console.log('Unexpected response:', response.data);
			}
		} catch (err) {
			console.error('Error starting crew chat:', err);
			setError('Failed to start crew chat. Please try again.');
		}
	};

	// Called when the user clicks "Start Experiment"
	const handleStartExperiment = async () => {
		setStartLoading(true);
		try {
			const response = await axios.post(
				`${API_URL}/api/experiment/${exptID}/start`,
				{
					userID: sessionData.userID,
					roomID: sessionData.roomID,
				},
				{ withCredentials: true }
			);

			if (response.status === 200) {
				console.log('Experiment started successfully.');
				setStarted(true);
			} else {
				setError('Failed to start experiment. Please try again.');
			}
		} catch (err) {
			if (err.response) {
				console.error('Error response from server:', err.response);
			} else if (err.request) {
				console.error('No response received:', err.request);
			} else {
				console.error('Error setting up request:', err.message);
			}
			setError('Failed to start experiment. Please try again.');
		} finally {
			setStartLoading(false);
		}
	};

	// Called when the user stops the experiment (via ExperimentUI).
	const handleStopExperiment = async () => {
		setEnded(true);
		try {
			await axios.post(
				`${API_URL}/api/experiment/${exptID}/end`,
				{
					userID: sessionData.userID,
					roomID: sessionData.roomID,
					metadata: getMetadata()
				},
				{ withCredentials: true }
			);
		} catch (err) {
			if (err.response) {
				console.error('Error response from server:', err.response);
			} else if (err.request) {
				console.error('No response received:', err.request);
			} else {
				console.error('Error setting up request:', err.message);
			}
			setError('Failed to start experiment. Please try again.');
		} finally {
			clearMetadata();
		}
	};

	// const handleBlockComplete = (responses) => {
	// 	// check if current block is conversation
	// 	const currentBlockType = sessionData.exptConfig.task.blocks[currentBlockIndex].type;
	// 	if (currentBlockType === 'scale' || currentBlockType === 'questionnaire') {

	// 		console.log("Submitting responses to server.");
	// 		submitResponses(responses);
	// 	} else if (currentBlockType === 'conversation') {
	// 		setConversationEnded(true);
	// 		console.log("Conversation block ended. Moving on to next block.");
	// 	};

	// 	if (sessionData.exptConfig && sessionData.exptConfig.task && sessionData.exptConfig.task.blocks && currentBlockIndex + 1 < sessionData.exptConfig.task.blocks.length) {
	// 		setCurrentBlockIndex(currentBlockIndex + 1);
	// 	} else {
	// 		console.log("All blocks completed. Ending experiment.");
	// 		handleStopExperiment()
	// 	}

	// };

	// reset stage when block changes
	useEffect(() => {
		setBlockStage('intro');
		setPendingResponses(null);
		setConversationEnded(false);
		setPreloadDone(false); setPreloading(false); setPreloadProgress(0);
		const block = sessionData.exptConfig?.task?.blocks?.[currentBlockIndex];
		if (block?.type === 'stimuli') {
			preloadStimuliForBlock(block);
		}
	}, [currentBlockIndex, sessionData.exptConfig?.task?.blocks]); // eslint-disable-line react-hooks/exhaustive-deps

	// handle completion of questionnaire/scale blocks
	const handleContentComplete = (responses) => {
		setPendingResponses(responses);
		setBlockStage('outro');
	};

	// handle completion of conversation blocks (mark ended, show Next Block button)
	const handleConversationComplete = () => {
		setPendingResponses(sessionData.messages[currentBlockIndex]);
		setConversationEnded(true);
	};
	// when user clicks Next Block after conversation
	const handleConversationNext = () => {
		setBlockStage('outro');
	};

	// advance to next block or end experiment
	const goToNextBlock = async () => {
		if (advancingBlock) return;
		setAdvancingBlock(true);
		try {
		const blocks = sessionData.exptConfig.task.blocks;
		const type = blocks[currentBlockIndex].type;
		const responsesToSubmit = pendingResponses;
		console.log(`submitting responses for ${type} block (${currentBlockIndex}):`, responsesToSubmit);
		await submitResponses(responsesToSubmit);
		const total = variantBlockCount || blocks.length;
		if (currentBlockIndex + 1 < total) {
			setCurrentBlockIndex(currentBlockIndex + 1);
		} else {
			console.log('All blocks completed (variant-aware). Ending experiment.');
			handleStopExperiment();
		}
		} finally {
			setAdvancingBlock(false);
		}
	};

	// derive current block and messages
	const blocksData = sessionData.exptConfig?.task?.blocks || [];
	const currentBlock = blocksData[currentBlockIndex];
	const raw = sessionData.messages?.[currentBlockIndex] || [];
	const blockMessages = Array.isArray(raw) ? raw : [];

	const handleMessagesChange = (updater) => {
		setSessionData(prev => {
			const prevMsgs = Array.isArray(prev.messages[currentBlockIndex])
				? prev.messages[currentBlockIndex]
				: [];
			const newMsgs = typeof updater === 'function'
				? updater(prevMsgs)
				: updater;
			return {
				...prev,
				messages: {
					...prev.messages,
					[currentBlockIndex]: newMsgs
				}
			};
		});
	};

	const renderStage = () => {
		if (!currentBlock) return null;
		const intro = currentBlock.introText || currentBlock.introduction_text || currentBlock.introduction || '';
		const outro = currentBlock.outroText || currentBlock.outroduction_text || currentBlock.outroduction || '';
		if (blockStage === 'intro') {
			return (
				<Box sx={{ minHeight:'calc(100vh - 32px)', display:'flex', alignItems:'center', justifyContent:'center' }}>
					<Instruction
						text={intro}
						buttonLabel={currentBlock.type==='stimuli' ? (preloading && !preloadDone ? 'Preloading...' : 'Start Block') : 'Start Block'}
						onContinue={() => setBlockStage('content')}
						disabled={currentBlock.type==='stimuli' && !preloadDone}
						progress={currentBlock.type==='stimuli' && preloading ? preloadProgress : null}
						progressLabel="Preparing stimuli"
					/>
				</Box>
			);
		}
		if (blockStage === 'content') {
			if (currentBlock.type === 'conversation') {
				return (
					<ConversationBlock
						sessionData={sessionData}
						currentBlockIndex={currentBlockIndex}
						chatMessages={blockMessages}
						onMessagesChange={handleMessagesChange}
						conversationEnded={conversationEnded}
						onStart={startCrew}
						onCrewFinished={handleConversationComplete}
						onNextBlock={handleConversationNext}
					/>
				);
			}
			if (currentBlock.type === 'primed_conversation') {
				// For now, start a crew session on each trial change from within the block component
				return (
					<PrimedConversationBlock
						sessionData={sessionData}
						currentBlockIndex={currentBlockIndex}
						block={currentBlock}
						onComplete={handleContentComplete}
						startCrew={startCrew}
						onStimulusEvent={postStimulusEvent}
						onGsaEvent={postGsaEvent}
					/>
				);
			}
			if (currentBlock.type === 'questionnaire' || currentBlock.type === 'scale') {
				return (
					<QuestionnaireComponent
						block={currentBlock}
						onComplete={handleContentComplete}
					/>
				);
			}
			if (currentBlock.type === 'stimuli') {
				return (
					<StimuliComponent
						stimuliSet={currentBlock.stimuliSet}
						stimuli={currentBlock.stimuli || currentBlock.files || []}
						blockId={currentBlock.id || currentBlock._id}
						questions={currentBlock.questions}
						settings={currentBlock.settings}
						randomization={currentBlock.randomization}
						shuffleSeed={`${sessionData.roomID || sessionData.userID || 'participant'}:${currentBlock.id || currentBlockIndex}`}
						onComplete={handleContentComplete}
						onStimulusEvent={postStimulusEvent}
					/>
				);
			}
			return null;
		}
		// outro stage
		if (blockStage === 'outro') {
			return (
				<Box sx={{ minHeight:'calc(100vh - 32px)', display:'flex', alignItems:'center', justifyContent:'center' }}>
					<Instruction
						text={outro}
						buttonLabel="Next Block"
						onContinue={goToNextBlock}
						disabled={advancingBlock}
					/>
				</Box>
			);
		}
		return null;
	};

	// Render a loading spinner while waiting for session data.
	if (loading) {
		return (
			<ThemeProvider theme={theme}>
				<Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<CircularProgress />
				</Box>
			</ThemeProvider>
		);
	}

	// Render an error message if session creation failed.
	if (error && !sessionData) {
		return (
			<ThemeProvider theme={theme}>
				<Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<Typography variant="h6" color="error">{error}</Typography>
				</Box>
			</ThemeProvider>
		);
	}

	// Show the welcome screen with the "Start Experiment" button if the subprocess isn’t started.
	if (!started) {
		const intro = sessionData.exptConfig?.task?.parameters?.introduction || sessionData.exptConfig?.task?.introduction_text || '';
		return (
			<ThemeProvider theme={theme}>
				<EnvironmentGuard onEnvironmentInfo={setEnvironmentInfo}>
				<Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<Container
						maxWidth="sm"
						sx={{ backgroundColor: '#fff', padding: 2, boxShadow: 1, borderRadius: 1 }}
					>
						<Typography variant="h6" align="center" gutterBottom>
							Welcome to the Experiment
						</Typography>
						<Typography variant="body1" align="center" gutterBottom>
							{intro}
						</Typography>
						{error && (
							<Typography variant="body2" align="center" color="error">
								{error}
							</Typography>
						)}
						<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
							<Button variant="contained" onClick={handleStartExperiment} disabled={startLoading}>
								{startLoading ? <CircularProgress size={24} /> : 'Start Experiment'}
							</Button>
						</Box>
					</Container>
				</Box>
				</EnvironmentGuard>
			</ThemeProvider>
		);
	}

	// If the experiment has ended, show the goodbye message.
	if (ended) {
		const debrief = sessionData.exptConfig?.task?.parameters?.debrief || sessionData.exptConfig?.task?.debrief_text || '';
		return (
			<ThemeProvider theme={theme}>
				<Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<Container
						maxWidth="sm"
						sx={{ backgroundColor: '#fff', padding: 2, boxShadow: 1, borderRadius: 1 }}
					>
						<Typography variant="h6" align="center" gutterBottom>
							Experiment Ended
						</Typography>
						<Typography variant="body1" align="center" gutterBottom>
							{debrief}
						</Typography>
					</Container>
				</Box>
			</ThemeProvider>
		);
	}

	// When the experiment is started and not ended, render the current block
	if (started && !ended) {
		return (
			<ThemeProvider theme={theme}>
				<EnvironmentGuard onEnvironmentInfo={setEnvironmentInfo}>
					{preflightStage === 'fullscreen' && !preflightCompleted && (
						<Box sx={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', p:3 }}>
							<Container maxWidth="sm" sx={{ bgcolor:'#fff', p:3, borderRadius:1, boxShadow:2 }}>
								<Typography variant="h6" gutterBottom>Enter Fullscreen</Typography>
								<Typography variant="body2" gutterBottom>Press the button below to enable fullscreen mode for consistent presentation.</Typography>
								<Box sx={{ textAlign:'right', mt:2 }}>
									<Button variant="contained" onClick={requestFullscreen}>Go Fullscreen</Button>
								</Box>
							</Container>
						</Box>
					)}
					{preflightStage === 'calibration' && !preflightCompleted && (
						<CalibrationScreen onConfirm={handleCalibrationConfirm} />
					)}
					{(preflightStage === null || preflightCompleted) && (
						<Box sx={{ mx:'auto', maxWidth:'var(--content-max-width)', p:2 }}>
							{renderStage()}
						</Box>
					)}
				</EnvironmentGuard>
			</ThemeProvider>
		);
	}

}

export default Experiment;
